import React, { useEffect, useState } from 'react';
import MatchTeamStats from './MatchTeamStats';
import RequestWordCloud from './RequestWordCloud';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    CircularProgress, 
    Chip,
    LinearProgress,
    Tabs,
    Tab,
    Alert,
    Tooltip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import {
    People as PeopleIcon,
    Group as GroupIcon,
    PersonAdd as PersonAddIcon,
    Assignment as AssignmentIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Sync as SyncIcon,
    SportsScore as ScoreIcon,
    PhoneAndroid as PhoneIcon,
    PhoneIphone as MobileIcon,
    LockOpen as OtpIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
    Refresh as RefreshIcon,
    Devices as DevicesIcon
} from '@mui/icons-material';
import type { DailyDashboardSummary, EnhancedDashboardStats, StatChange } from '../../models';
import { dashboardApi } from '../../services/api';

const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    change?: StatChange;
    subtitle?: string;
}> = ({ title, value, icon, color, change, subtitle }) => {
    const getTrendIcon = () => {
        if (!change) return null;
        const { change_percentage } = change;
        if (change_percentage > 0) return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
        if (change_percentage < 0) return <TrendingDownIcon sx={{ color: '#f44336' }} />;
        return <TrendingFlatIcon sx={{ color: '#9e9e9e' }} />;
    };

    const getTrendColor = () => {
        if (!change) return 'default';
        const { change_percentage } = change;
        if (change_percentage > 0) return 'success';
        if (change_percentage < 0) return 'error';
        return 'default';
    };

    return (
        <Card sx={{ height: '100%', position: 'relative' }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Box
                        sx={{
                            backgroundColor: `${color}20`,
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                        }}
                    >
                        {icon}
                    </Box>
                    <Box flex={1}>
                        <Typography variant="h6" component="div">
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {change && (
                        <Tooltip title={`${change.change_percentage > 0 ? '+' : ''}${change.change_percentage.toFixed(1)}% from same time yesterday`}>
                            <Chip
                                icon={getTrendIcon() || undefined}
                                label={`${change.change_percentage > 0 ? '+' : ''}${change.change_percentage.toFixed(1)}%`}
                                color={getTrendColor() as any}
                                size="small"
                                variant="outlined"
                            />
                        </Tooltip>
                    )}
                </Box>
                <Typography variant="h4" component="div">
                    {value.toLocaleString()}
                </Typography>
                {change && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Same time yesterday: {change.previous.toLocaleString()}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const TrendChart: React.FC<{
    data: any[];
    dataKey: string;
    title: string;
    color: string;
    matches?: any[];
}> = ({ data, dataKey, title, color, matches = [] }) => {
    if (!data || data.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {title}
                    </Typography>
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No data available
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const values = data.map(d => d[dataKey]).filter(v => v !== null && v !== undefined);
    if (values.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {title}
                    </Typography>
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No valid data points
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Format data for Recharts
    const chartData = data.map((item) => ({
        time: new Date(item.timestamp).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true
        }),
        value: item[dataKey],
        timestamp: new Date(item.timestamp).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true
        }),
        fullTimestamp: item.timestamp
    }));
    

    // Calculate Y-axis domain with padding
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const range = maxValue - minValue;
    const padding = range * 0.1; // 10% padding
    const yAxisDomain = [Math.max(0, minValue - padding), maxValue + padding];

    // Process matches for the chart
    const matchMarkers = matches.map((match) => {
        const matchDateTime = new Date(match.start_date);
        const matchTime = matchDateTime.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true
        });
        
        // Find the closest data point by time difference
        let closestDataPoint = chartData[0];
        let minTimeDiff = Infinity;
        
        chartData.forEach(dataPoint => {
            const dataPointTime = new Date(dataPoint.fullTimestamp);
            const timeDiff = Math.abs(dataPointTime.getTime() - matchDateTime.getTime());
            if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                closestDataPoint = dataPoint;
            }
        });
        
        // Determine match color based on format
        let matchColor = '#9e9e9e'; // Default dull color
        if (match.match_format?.toLowerCase().includes('t20')) {
            matchColor = '#ff5722'; // High highlight for T20
        } else if (match.match_format?.toLowerCase().includes('odi')) {
            matchColor = '#ff9800'; // Medium highlight for ODI
        }
        
        // If the time difference is too large (more than 2 hours), skip this match
        if (minTimeDiff > 2 * 60 * 60 * 1000) { // 2 hours in milliseconds
            return null;
        }
        
        return {
            time: closestDataPoint.time, // Use the closest data point time for positioning
            actualMatchTime: matchTime, // Keep actual match time for display
            value: closestDataPoint.value,
            match: match,
            color: matchColor
        };
    }).filter((marker): marker is NonNullable<typeof marker> => marker !== null); // Remove null values

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            // Check if this is a match marker
            const matchMarker = matchMarkers.find(m => m.time === label);
            
            if (matchMarker) {
                return (
                    <Box sx={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1, 
                        p: 2,
                        boxShadow: 2,
                        maxWidth: 250
                    }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                            🏏 Cricket Match
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Time: {matchMarker.actualMatchTime}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                            {matchMarker.match.name || matchMarker.match.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Series: {matchMarker.match.series__name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Format: {matchMarker.match.match_format}
                        </Typography>
                    </Box>
                );
            }
            
            // Regular data point tooltip
            return (
                <Box sx={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 1,
                    boxShadow: 2
                }}>
                    <Typography variant="body2" color="text.secondary">
                        Time: {payload[0].payload.timestamp}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                        Value: {payload[0].value.toLocaleString()}
                    </Typography>
                </Box>
            );
        }
        return null;
    };
    
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Box sx={{ height: 350, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 40, left: 40, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis 
                                dataKey="time" 
                                stroke="#666"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                interval={2}
                            />
                            <YAxis 
                                stroke="#666"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(value) => value.toLocaleString()}
                                domain={yAxisDomain}
                                tickCount={6}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Line 
                                type="monotone" 
                                dataKey="value" 
                                stroke={color}
                                strokeWidth={3}
                                dot={{ fill: color, strokeWidth: 2, r: 5 }}
                                activeDot={{ r: 7, stroke: color, strokeWidth: 2, fill: 'white' }}
                            />
                            {/* Add match markers */}
                            {matchMarkers.map((marker, index) => (
                                <ReferenceLine
                                    key={index}
                                    x={marker.time}
                                    stroke={marker.color}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    label={{
                                        value: "🏏",
                                        position: "top",
                                        style: { fontSize: '12px' }
                                    }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Current: {data[data.length - 1]?.[dataKey]?.toLocaleString() || 'N/A'}
                    </Typography>
                        <Typography variant="caption" color="text.secondary">
                        Range: {Math.min(...values).toLocaleString()} - {Math.max(...values).toLocaleString()}
                        </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const DailyTrendChart: React.FC<{
    summary: DailyDashboardSummary | null;
    scaleType: 'linear' | 'log';
}> = ({ summary, scaleType }) => {
    if (!summary || !summary.data || summary.data.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Daily Summary Trends
                    </Typography>
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No daily summary data available
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const chartData = summary.data.map((item) => ({
        formattedDate: new Date(item.date).toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
        }),
        ...item,
    }));

    const numericValues = (key: keyof typeof summary.data[number]) =>
        summary.data
            .map((d) => d[key])
            .filter((v): v is number => typeof v === 'number');

    const allValues = [
        ...numericValues('app_installed'),
        ...numericValues('new_users'),
        ...numericValues('verified_otps'),
        ...numericValues('matches_with_team'),
        ...numericValues('today_active_clients'),
        ...numericValues('fantasy_account_linked'),
    ];

    if (allValues.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Daily Summary Trends
                    </Typography>
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No numeric data available for the selected range
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);

    let yAxisDomain: [number, number] = [0, maxValue || 1];
    let yAxisScale: 'linear' | 'log' = 'linear';

    if (scaleType === 'log') {
        const positiveValues = allValues.filter((v) => v > 0);
        if (positiveValues.length > 0) {
            const minPositive = Math.min(...positiveValues);
            const maxPositive = Math.max(...positiveValues);
            yAxisScale = 'log';
            yAxisDomain = [Math.max(minPositive / 2, 0.1), maxPositive * 2];
        } else {
            // Fallback to linear if no positive values
            const range = maxValue - minValue;
            const padding = range * 0.1;
            yAxisDomain = [Math.max(0, minValue - padding), maxValue + padding];
        }
    } else {
        const range = maxValue - minValue;
        const padding = range * 0.1;
        yAxisDomain = [Math.max(0, minValue - padding), maxValue + padding];
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Daily Summary Trends ({summary.range_days} days)
                </Typography>
                <Box sx={{ height: 350, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 40, left: 40, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="formattedDate"
                                stroke="#666"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                stroke="#666"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(value) => value.toLocaleString()}
                                domain={yAxisDomain}
                                scale={yAxisScale}
                                allowDataOverflow={scaleType === 'log'}
                                tickCount={6}
                            />
                            <RechartsTooltip
                                formatter={(value: any) => (typeof value === 'number' ? value.toLocaleString() : value)}
                            />
                            <Line
                                type="monotone"
                                dataKey="app_installed"
                                name="App Installs (per day)"
                                stroke="#2e7d32"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="new_users"
                                name="New Users (per day)"
                                stroke="#1976d2"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="verified_otps"
                                name="Verified OTPs"
                                stroke="#0288d1"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="matches_with_team"
                                name="Matches with Teams"
                                stroke="#ff9800"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="today_active_clients"
                                name="Active Clients (per day)"
                                stroke="#9c27b0"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="fantasy_account_linked"
                                name="Fantasy Accounts Linked"
                                stroke="#e91e63"
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Range: {summary.start_date} to {summary.end_date}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const ClientPlatformCard: React.FC<{
    clientDetails: any;
}> = ({ clientDetails }) => {
    const platformData = [
        { name: 'Android', value: clientDetails.platforms.android, color: '#4caf50' },
        { name: 'iOS', value: clientDetails.platforms.ios, color: '#2196f3' },
        { name: 'Web', value: clientDetails.platforms.web, color: '#ff9800' }
    ];

    const total = platformData.reduce((sum, item) => sum + item.value, 0);

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Client Platforms
                </Typography>
                <Box display="flex" alignItems="center" mb={2}>
                    <DevicesIcon sx={{ mr: 1, color: '#666' }} />
                    <Typography variant="h4">
                        {clientDetails.total_clients.toLocaleString()}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Today Active: {clientDetails.today_active.toLocaleString()}
                </Typography>
                
                <Box mt={2}>
                    {platformData.map((platform) => {
                        const percentage = total > 0 ? (platform.value / total) * 100 : 0;
                        return (
                            <Box key={platform.name} sx={{ mb: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="body2">
                                        {platform.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {platform.value.toLocaleString()} ({percentage.toFixed(1)}%)
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={percentage} 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        backgroundColor: `${platform.color}20`,
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: platform.color
                                        }
                                    }} 
                                />
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};

const IntervalInfoCard: React.FC<{
    intervalInfo: any;
}> = ({ intervalInfo }) => (
    <Card>
        <CardContent>
            <Typography variant="h6" gutterBottom>
                Data Collection Info
            </Typography>
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    Average Interval
                </Typography>
                <Typography variant="h5">
                    {intervalInfo.avg_interval_hours}h
                </Typography>
            </Box>
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    Data Points
                </Typography>
                <Typography variant="h6">
                    {intervalInfo.data_points}
                </Typography>
            </Box>
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    Time Range
                </Typography>
                <Typography variant="h6">
                    {intervalInfo.time_range_hours}h
                </Typography>
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary">
                    Min: {intervalInfo.min_interval_hours}h | Max: {intervalInfo.max_interval_hours}h
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

const Dashboard: React.FC = () => {
    const [enhancedStats, setEnhancedStats] = useState<EnhancedDashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState(24); // hours
    const [activeTab, setActiveTab] = useState(0);
    const [dailySummary, setDailySummary] = useState<DailyDashboardSummary | null>(null);
    const [dailyRange, setDailyRange] = useState<number>(7);
    const [dailyScale, setDailyScale] = useState<'linear' | 'log'>('log');

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const { data } = await dashboardApi.getStats({ 
                hours_back: timeRange, 
                limit: timeRange * 6 // 6 data points per hour (10-minute intervals)
            });
            setEnhancedStats(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard statistics');
        } finally {
            setLoading(false);
        }
    };

    const fetchDailySummary = async (days: number = dailyRange) => {
        try {
            const { data } = await dashboardApi.getDailySummary({ days });
            setDailySummary(data);
        } catch (err) {
            console.error('Error fetching daily dashboard summary:', err);
        }
    };

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, 300000); // Refresh every 5 minutes

        return () => clearInterval(interval);
    }, [timeRange]);

    useEffect(() => {
        // Fetch daily summary once when dashboard mounts and whenever range changes
        fetchDailySummary(dailyRange);
    }, [dailyRange]);

    if (loading && !enhancedStats) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!enhancedStats) {
        return (
            <Typography variant="h6" color="error" align="center">
                No data available
            </Typography>
        );
    }

    const { current, changes, client_details, interval_info, historical, cricket_matches } = enhancedStats;

    return (
        <Box p={3}>
            {/* Header with controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Dashboard Analytics
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            label="Time Range"
                            onChange={(e) => setTimeRange(Number(e.target.value))}
                        >
                            <MenuItem value={6}>6 Hours</MenuItem>
                            <MenuItem value={12}>12 Hours</MenuItem>
                            <MenuItem value={24}>24 Hours</MenuItem>
                            <MenuItem value={48}>48 Hours</MenuItem>
                            <MenuItem value={168}>7 Days</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh Data">
                        <IconButton onClick={fetchStats} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Last updated info */}
            <Typography variant="subtitle2" color="textSecondary" gutterBottom align="right">
                Last updated: {new Date(current.timestamp).toLocaleString()} | 
                Data points: {interval_info.data_points} | 
                Avg interval: {interval_info.avg_interval_hours}h
                {enhancedStats.metadata.comparison.time_difference_hours !== null && (
                    <> | Comparison accuracy: ±{enhancedStats.metadata.comparison.time_difference_hours}h</>
                )}
            </Typography>

            {/* Tabs for different views */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                    <Tab label="Overview" />
                    <Tab label="Trends" />
                    <Tab label="Clients" />
                    <Tab label="System" />
                    <Tab label="Match Teams" />
                    <Tab label="Request Cloud" />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* User Stats */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            User Statistics
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Total Users"
                            value={current.total_users}
                            icon={<PeopleIcon sx={{ color: '#1976d2' }} />}
                            color="#1976d2"
                            change={changes.total_users}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Active Clients"
                            value={current.today_active_clients}
                            icon={<GroupIcon sx={{ color: '#2e7d32' }} />}
                            color="#2e7d32"
                            change={changes.today_active_clients}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="New Users"
                            value={current.new_users}
                            icon={<PersonAddIcon sx={{ color: '#ed6c02' }} />}
                            color="#ed6c02"
                            change={changes.new_users}
                        />
                    </Grid>

                    {/* Team Stats */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Team Statistics
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Teams Created"
                            value={current.teams_created}
                            icon={<GroupIcon sx={{ color: '#9c27b0' }} />}
                            color="#9c27b0"
                            change={changes.teams_created}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Teams Synced"
                            value={current.teams_synced}
                            icon={<SyncIcon sx={{ color: '#f57c00' }} />}
                            color="#f57c00"
                            change={changes.teams_synced}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Matches with Teams"
                            value={current.matches_with_team}
                            icon={<ScoreIcon sx={{ color: '#388e3c' }} />}
                            color="#388e3c"
                            change={changes.matches_with_team}
                        />
                    </Grid>

                    {/* App Stats */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            App Statistics
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Total App Installs"
                            value={current.total_app_installed}
                            icon={<PhoneIcon sx={{ color: '#7b1fa2' }} />}
                            color="#7b1fa2"
                            change={changes.total_app_installed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Today's App Installs"
                            value={current.today_app_installed}
                            icon={<MobileIcon sx={{ color: '#c2185b' }} />}
                            color="#c2185b"
                            change={changes.today_app_installed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Verified OTPs"
                            value={current.verification_otps_sent}
                            icon={<OtpIcon sx={{ color: '#0288d1' }} />}
                            color="#0288d1"
                            change={changes.verification_otps_sent}
                        />
                    </Grid>
                </Grid>
            )}

            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Daily Range</InputLabel>
                            <Select
                                value={dailyRange}
                                label="Daily Range"
                                onChange={(e) => setDailyRange(Number(e.target.value))}
                            >
                                <MenuItem value={7}>Last 7 Days</MenuItem>
                                <MenuItem value={30}>Last 30 Days</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Scale</InputLabel>
                            <Select
                                value={dailyScale}
                                label="Scale"
                                onChange={(e) => setDailyScale(e.target.value as 'linear' | 'log')}
                            >
                                <MenuItem value="linear">Linear</MenuItem>
                                <MenuItem value="log">Logarithmic</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <DailyTrendChart summary={dailySummary} scaleType={dailyScale} />
                    </Grid>
                    <Grid item xs={12}>
                        <TrendChart
                            data={[...historical].reverse()}
                            dataKey="total_users"
                            title="Total Users Trend (24h)"
                            color="#1976d2"
                            matches={cricket_matches}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TrendChart
                            data={[...historical].reverse()}
                            dataKey="total_app_installed"
                            title="Total App Installs (24h)"
                            color="#2e7d32"
                            matches={cricket_matches}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TrendChart
                            data={[...historical].reverse()}
                            dataKey="total_active_clients"
                            title="Active Clients Trend (24h)"
                            color="#ff9800"
                            matches={cricket_matches}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TrendChart
                            data={[...historical].reverse()}
                            dataKey="today_app_installed"
                            title="Today's App Installs Trend (24h)"
                            color="#7b1fa2"
                            matches={cricket_matches}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TrendChart
                            data={[...historical].reverse()}
                            dataKey="new_users"
                            title="New Users Trend (24h)"
                            color="#ed6c02"
                            matches={cricket_matches}
                        />
                    </Grid>
                    <Grid item xs={12}>
                    </Grid>
                </Grid>
            )}

            {activeTab === 2 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ClientPlatformCard clientDetails={client_details} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <IntervalInfoCard intervalInfo={interval_info} />
                    </Grid>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                    App Version Distribution
                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                        Scale: 0 to {Math.ceil(Math.max(...Object.values(client_details.app_versions).map(data => 
                                            Math.max(
                                                typeof data === 'object' && data !== null ? (data as any).all || 0 : (typeof data === 'number' ? data : 0),
                                                typeof data === 'object' && data !== null ? (data as any).active || 0 : 0
                                            )
                                        ), 0) / 1000) * 1000} users
                                    </Typography>
                                </Box>
                                <Box mt={2}>
                                    {(() => {
                                        // Calculate consistent scale based on max values
                                        const versionData = Object.entries(client_details.app_versions).map(([version, data]) => {
                                            const allClients = typeof data === 'object' && data !== null ? (data as any).all || 0 : (typeof data === 'number' ? data : 0);
                                            const activeClients = typeof data === 'object' && data !== null ? (data as any).active || 0 : 0;
                                            return { version, allClients, activeClients };
                                        });

                                        const maxAllClients = Math.max(...versionData.map(v => v.allClients), 0);
                                        const maxActiveClients = Math.max(...versionData.map(v => v.activeClients), 0);
                                        const maxValue = Math.max(maxAllClients, maxActiveClients);
                                        
                                        // Ceil to next 1000
                                        const scaleMax = Math.ceil(maxValue / 1000) * 1000;

                                        return versionData
                                            .sort((a, b) => b.activeClients - a.activeClients)
                                            .map(({ version, allClients, activeClients }) => {
                                                const activePercentage = allClients > 0 ? (activeClients / allClients) * 100 : 0;
                                                const allClientsWidth = (allClients / scaleMax) * 100;
                                                const activeClientsWidth = (activeClients / scaleMax) * 100;
                                                
                                                return (
                                                    <Box key={version} sx={{ mb: 2 }}>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                            <Typography variant="body2" fontWeight="medium">
                                                    {version}
                                                </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {activeClients.toLocaleString()} / {allClients.toLocaleString()} ({activePercentage.toFixed(1)}% active)
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                                                            {/* All clients background (dim) */}
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    height: '100%',
                                                                    width: `${allClientsWidth}%`,
                                                                    backgroundColor: '#e0e0e0',
                                                                    borderRadius: 4
                                                                }}
                                                            />
                                                            {/* Active clients overlay (dark) */}
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    height: '100%',
                                                                    width: `${activeClientsWidth}%`,
                                                                    backgroundColor: '#1976d2',
                                                                    borderRadius: 4,
                                                                    transition: 'width 0.3s ease'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            });
                                    })()}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {activeTab === 3 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Total Tasks"
                            value={current.tasks}
                            icon={<AssignmentIcon sx={{ color: '#1976d2' }} />}
                            color="#1976d2"
                            change={changes.tasks}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Failed Tasks"
                            value={current.failed_tasks}
                            icon={<ErrorIcon sx={{ color: '#d32f2f' }} />}
                            color="#d32f2f"
                            change={changes.failed_tasks}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Pending Actions (Cricket)"
                            value={current.pending_ma_cricket}
                            icon={<PendingIcon sx={{ color: '#ff9800' }} />}
                            color="#ff9800"
                            change={changes.pending_ma_cricket}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Pending Actions (Football)"
                            value={current.pending_ma_football}
                            icon={<PendingIcon sx={{ color: '#ff9800' }} />}
                            color="#ff9800"
                            change={changes.pending_ma_football}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Total Matches"
                            value={current.total_matches}
                            icon={<ScoreIcon sx={{ color: '#388e3c' }} />}
                            color="#388e3c"
                            change={changes.total_matches}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Users with Teams"
                            value={current.users_with_team}
                            icon={<PeopleIcon sx={{ color: '#9c27b0' }} />}
                            color="#9c27b0"
                            change={changes.users_with_team}
                        />
                    </Grid>
                </Grid>
            )}

            {activeTab === 4 && (
                <MatchTeamStats />
            )}

            {activeTab === 5 && (
                <RequestWordCloud />
            )}

            {loading && (
                <Box position="fixed" top={0} left={0} right={0}>
                    <LinearProgress />
                </Box>
            )}
        </Box>
    );
};

export default Dashboard;